import { isGeoPointValid } from '@@partner:helpers/util'
import { GeoPoint, GeoZone, OrderInfo } from '@@partner:types:app'
import L, { LatLngLiteral, Marker as LeafletEditableMarker,
  Polygon as LeafletEditablePolygon,
  Map as LeafletMap, } from 'leaflet'
import React, { useState } from 'react'
import { MapContainer, Marker, Polygon, Popup, TileLayer } from 'react-leaflet'
import LegacyCrosshairsIcon from '@rsuite/icons/legacy/Crosshairs'
import LegacyMapMarkerIcon from '@rsuite/icons/legacy/MapMarker'
import LegacyRefreshIcon from '@rsuite/icons/legacy/Refresh'

import MarkerIcon from './marker-icon.png'
import MarkerIconRetina from './marker-icon-2x.png'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { getCenter} from 'geolib'
import AppLoader from '@components/common/AppLoader'
import { generatePath, Link, useParams } from 'react-router-dom'
import { ROUTE_ORDERS_EDIT_ORDER, ROUTE_ORDERS_VIEW_ORDER } from '@@partner:config/routes'
import MapMarker from '@components/common/MapMarker'


export const DEFAULT_MAP_CENTER: GeoPoint = {
  latitude: 50.326044,
  longitude: 48.480974
}

/*
  Default map zoom
*/

const DEFAULT_MAP_ZOOM = 12

const MARKER_ICON = new L.Icon({
  iconUrl: MarkerIcon,
  iconRetinaUrl: MarkerIconRetina,
  iconSize: new L.Point(24, 36)
})

/*
  Convert polygon coords into leaflet polygon
*/

const makePolygonFromZone = (zone: GeoPoint[]) =>
  L.polygon(convertGeoPointsZoneToLeafletZone(zone), { stroke: true })

/*
  Convert geo zone to leaflet compatible positions
*/

const convertGeoPointsZoneToLeafletZone = (zone: GeoPoint[]) =>
  zone.map((value) => convertGeoPointToLeafletLatLng(value))

/*
  Convert our point format to leaflet compatible
*/

const convertGeoPointToLeafletLatLng = (point: GeoPoint): LatLngLiteral => ({
  lat: point.latitude as number,
  lng: point.longitude as number
})

/*
  Convert leaflet coords to our format
*/

const convertLeafletLatLngToGeoPoint = (
  latLngExpr: LatLngLiteral
): GeoPoint => ({
  latitude: latLngExpr.lat,
  longitude: latLngExpr.lng
})

/*
  Get map center by centerPoint
*/

const makeMapCenter = (centerPoint: GeoPoint): LatLngLiteral =>
  convertGeoPointToLeafletLatLng(
    isGeoPointValid(centerPoint) ? centerPoint : DEFAULT_MAP_CENTER
  )

type Props = {
  order: OrderInfo
  loading?: boolean
}

export default function OrderOnMap ({
  order,
  loading
}: Props) {
  const { brandSlug } = useParams<any>()
  // map center state
  const [ mapCenter, setMapCenter ] = useState(() => makeMapCenter(DEFAULT_MAP_CENTER))

  const positions = [
    {
      color: 'red',
      point: convertGeoPointToLeafletLatLng(order.customerGeoPoint),
    }
  ]
  if (order.shop?.shopGeoPoint) {
    positions.push({
      color: 'blue',
      point: convertGeoPointToLeafletLatLng(order.shop?.shopGeoPoint)
    })
  }
  const bounds: [ number, number ][] = positions.map(point => [ point.point?.lat, point.point?.lng ])
  const center = getCenter(positions.map(position => position.point))
  const preparedCenter = center ? convertGeoPointToLeafletLatLng(center) : mapCenter

  if (loading) {
    return <AppLoader.Inline />
  }
  
  return (
    <MapContainer
      bounds={bounds}
      center={preparedCenter}
      scrollWheelZoom={false}
      editable={true}
      style={{
        height: 400,
        width: '100%'
      }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {positions && positions.length > 0 && (
        positions.map(position => (
          <Marker
            icon={MapMarker(position.color)}
            position={position.point}
          />
        ))
      )}
    </MapContainer>
  )
}